import React, { FC, useState } from "react";
import {
  Box,
  Button,
  Icon,
  Paper,
  alpha,
  IconButton,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../../../components/PageHeader/PageHeader";
import { kitchenService } from "../../../services/kitchen.service";
import { toast } from "react-toastify";
import { inventoryService } from "../../../services/inventory.service";
import { RecipeFormData, IngredientOption, ExtendedBatch } from "./types";
import { RecipeEditPanel } from "./RecipeEditPanel";
import { RecipeGraphPanel } from "./RecipeGraphPanel";
import { Node } from "reactflow";
import { IngredientDialog } from "./components/IngredientDialog";
import { Stepper, Step } from "../../../../../components/Stepper/Stepper";

export const RecipeBuilder: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState<string>("01");
  const [formErrors, setFormErrors] = React.useState({
    name: false,
    category: false,
  });
  const [ingredientDialogOpen, setIngredientDialogOpen] = React.useState(false);
  const [ingredientOptions, setIngredientOptions] = React.useState<
    IngredientOption[]
  >([]);

  const [showGraph, setShowGraph] = React.useState(false);

  const [recipe, setRecipe] = React.useState<RecipeFormData>({
    name: "",
    description: "",
    category: "",
    servings: 1,
    ingredients: [],
    instructions: [],
    notes: "",
    tags: [],
    yield_quantity: 0,
    yield_unit: "",
    total_preparing_time: 0,
    total_cooking_time: 0,
    is_active: true,
  });

  const [editingIngredientIndex, setEditingIngredientIndex] = React.useState<
    number | null
  >(null);

  const [batchDataCache, setBatchDataCache] = React.useState<
    Record<
      string,
      {
        batches: ExtendedBatch[];
        totalCost: number;
        averageCost: number;
        totalQuantity: number;
        lastFetched: number;
      }
    >
  >({});

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const getBatchData = React.useCallback(
    async (ingredientId: string) => {
      const cachedData = batchDataCache[ingredientId];
      const now = Date.now();
      if (cachedData && now - cachedData.lastFetched < 5 * 60 * 1000) {
        return cachedData;
      }

      try {
        const batchInfo = await loadIngredientBatches(ingredientId);
        if (batchInfo) {
          const newData = {
            ...batchInfo,
            lastFetched: now,
          };
          setBatchDataCache((prev) => ({
            ...prev,
            [ingredientId]: newData,
          }));
          return newData;
        }
      } catch (error) {
        console.error("Error loading batch data:", error);
        toast.error("Failed to load batch data");
      }
      return null;
    },
    [batchDataCache]
  );

  const loadIngredientBatches = async (ingredientId: string) => {
    try {
      const response = await inventoryService.listBatches({
        ingredient_id: ingredientId,
        pageIndex: 0,
        pageSize: 100,
        sortField: "expiry_date",
        sortOrder: "asc",
        include_wasted: false,
      });

      const batches: any[] = response.data.batches;
      const totalCost = batches.reduce(
        (sum, batch) => sum + (batch.total_cost || 0),
        0
      );
      const totalQuantity = batches.reduce(
        (sum, batch) => sum + batch.remaining_quantity,
        0
      );
      const averageCost = totalQuantity > 0 ? totalCost / totalQuantity : 0;

      return {
        batches,
        totalCost,
        averageCost,
        totalQuantity,
      };
    } catch (error) {
      console.error("Error loading batches:", error);
      toast.error("Failed to load batches");
      return null;
    }
  };

  React.useEffect(() => {
    if (id) {
      loadRecipe();
    }
  }, [id]);

  const loadRecipe = async () => {
    try {
      if (!id) return;
      setLoading(true);
      const response = await kitchenService.getRecipe(id);
      setRecipe(response.data);
    } catch (error) {
      console.error("Error loading recipe:", error);
      toast.error("Failed to load recipe");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {
      name: !recipe.name.trim(),
      category: !recipe.category.trim(),
    };
    setFormErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (recipe.ingredients.length === 0) {
      toast.error("Please add at least one ingredient");
      return;
    }

    try {
      setSaving(true);
      if (id) {
        console.log(JSON.stringify(recipe, null, 2));
        await kitchenService.updateRecipe({
          _id: id,
          name: recipe.name,
          description: recipe.description,
          category: recipe.category,
          servings: recipe.servings,
          yield_quantity: recipe.yield_quantity,
          yield_unit: recipe.yield_unit,
          total_preparing_time: recipe.total_preparing_time,
          total_cooking_time: recipe.total_cooking_time,
          instructions: recipe.instructions,
          ingredients: recipe.ingredients,
          tags: recipe.tags,
          is_active: recipe.is_active,
          notes: recipe.notes,
        });
        toast.success("Recipe updated successfully");
      } else {
        await kitchenService.createRecipe(recipe);
        toast.success("Recipe created successfully");
      }
      navigate("/business/kitchen/recipes");
    } catch (error) {
      console.error("Error saving recipe:", error);
      toast.error("Failed to save recipe");
    } finally {
      setSaving(false);
    }
  };

  const loadIngredients = async () => {
    try {
      setLoading(true);
      const response = await inventoryService.listIngredients({
        pageIndex: 0,
        pageSize: 100,
        sortField: "created_at",
        sortOrder: "desc",
      });

      const ingredients: IngredientOption[] = response.data.map(
        (item: any) => ({
          id: item.ingredient_id,
          _id: item._id,
          ingredient_id: item.ingredient_id,
          name: item.name,
          unit: item.unit,
          type: "ingredient",
          description: item.description,
          stock_quantity: item.stock_quantity,
        })
      );

      setIngredientOptions(ingredients);
    } catch (error) {
      console.error("Error loading ingredients:", error);
      toast.error("Failed to load ingredients");
    } finally {
      setLoading(false);
    }
  };

  const handleEditIngredient = (index: number) => {
    const ingredient = recipe.ingredients[index];
    const ingredientOption = ingredientOptions.find(
      (opt) => opt.id === ingredient.item_id
    );

    if (ingredientOption) {
      setEditingIngredientIndex(index);
      setIngredientDialogOpen(true);
      loadIngredients();
    }
  };

  const handleNodeClick = (event: React.MouseEvent, node: Node) => {
    if (node.id === "recipe") return;

    if (node.id.startsWith("ing-")) {
      // Toggle expansion state
      // setSelectedNode(node);
      // setBatchesDialogOpen(true);
    }
  };

  const handleSaveIngredient = (
    data: Array<{
      ingredient: IngredientOption;
      quantity: number;
      unit: string;
      notes: string;
      selectedBatch?: ExtendedBatch;
    }>
  ) => {
    setRecipe((prev) => {
      const newIngredients = [...prev.ingredients];
      if (editingIngredientIndex !== null) {
        // Edit existing ingredient with first item from array
        if (data.length > 0) {
          const ingredient = data[0];
          newIngredients[editingIngredientIndex] = {
            item_id: ingredient.ingredient.id,
            item_type: ingredient.ingredient.type,
            quantity: ingredient.quantity,
            unit: ingredient.unit,
            parent_unit: ingredient.unit,
            notes: ingredient.notes,
            ingredient_id: ingredient.ingredient.ingredient_id,
            selected_batch_id: ingredient.selectedBatch?.batch_id,
            brand_name: ingredient.ingredient.name,
          };
        }
      } else {
        // Add new ingredients
        const newItems = data.map((ingredient) => ({
          item_id: ingredient.ingredient.id,
          item_type: ingredient.ingredient.type,
          quantity: ingredient.quantity,
          unit: ingredient.unit,
          parent_unit: ingredient.unit,
          notes: ingredient.notes,
          ingredient_id: ingredient.ingredient.ingredient_id,
          selected_batch_id: ingredient.selectedBatch?.batch_id,
          brand_name: ingredient.ingredient.name,
        }));
        newIngredients.push(...newItems);
      }
      return {
        ...prev,
        ingredients: newIngredients,
      };
    });

    setIngredientDialogOpen(false);
    setEditingIngredientIndex(null);
  };

  const handleCloseIngredientDialog = () => {
    setIngredientDialogOpen(false);
    setEditingIngredientIndex(null);
  };

  const handleOpenAddIngredient = () => {
    setIngredientDialogOpen(true);
    loadIngredients();
  };

  const steps: Step[] = [
    {
      id: "01",
      name: "Basic Information",
      description: "Recipe details, servings, and info",
      icon: "restaurant_menu",
      status:
        currentStep === "01"
          ? "current"
          : parseInt(currentStep) > 1
          ? "complete"
          : "upcoming",
    },
    {
      id: "02",
      name: "Ingredients",
      description: "Add ingredients and view recipe composition",
      icon: "inventory",
      status:
        currentStep === "02"
          ? "current"
          : parseInt(currentStep) > 2
          ? "complete"
          : "upcoming",
    },
    {
      id: "03",
      name: "Instructions",
      description: "Step-by-step cooking instructions",
      icon: "format_list_numbered",
      status: currentStep === "03" ? "current" : "upcoming",
    },
  ];

  const handleStepClick = (stepId: string) => {
    const stepNumber = parseInt(stepId);
    const currentStepNumber = parseInt(currentStep);

    // Can always go back
    if (stepNumber < currentStepNumber) {
      setCurrentStep(stepId);
      setShowGraph(false);
      return;
    }

    // Can only move forward if current step is complete
    if (stepNumber === currentStepNumber + 1) {
      if (currentStep === "01" && (!recipe.name || !recipe.category)) {
        toast.warning(
          "Please fill in all required fields in Basic Information"
        );
        return;
      }
      if (currentStep === "02" && recipe.ingredients.length === 0) {
        toast.warning("Please add at least one ingredient");
        return;
      }
      setCurrentStep(stepId);
      setShowGraph(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "01":
        return (
          <Box sx={{ margin: "0 auto" }}>
            <RecipeEditPanel
              recipe={recipe}
              onChange={setRecipe}
              formErrors={formErrors}
              mode="basic"
            />
          </Box>
        );
      case "02":
        return (
          <Box sx={{ height: "calc(100vh - 300px)", overflow: "auto" }}>
            {showGraph ? (
              <RecipeGraphPanel
                recipe={recipe}
                ingredientOptions={ingredientOptions}
                onNodeClick={handleNodeClick}
                batchDataCache={batchDataCache}
                getBatchData={getBatchData}
              />
            ) : (
              <Box sx={{ margin: "0 auto" }}>
                <RecipeEditPanel
                  recipe={recipe}
                  onChange={setRecipe}
                  formErrors={formErrors}
                  mode="ingredients"
                  onAddIngredient={handleOpenAddIngredient}
                  onEditIngredient={handleEditIngredient}
                  ingredientOptions={ingredientOptions}
                  batchDataCache={batchDataCache}
                  getBatchData={getBatchData}
                />
              </Box>
            )}
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={() => setShowGraph(!showGraph)}
                startIcon={<Icon>{showGraph ? "edit" : "account_tree"}</Icon>}
              >
                {showGraph ? "Edit Ingredients" : "View Recipe Graph"}
              </Button>
            </Box>
          </Box>
        );
      case "03":
        return (
          <Box sx={{ margin: "0 auto" }}>
            <RecipeEditPanel
              recipe={recipe}
              onChange={(updatedRecipe) => {
                // Calculate total times from instructions
                const totalPrepTime = updatedRecipe.instructions.reduce(
                  (total, instruction) => total + instruction.preparation_time,
                  0
                );
                const totalCookTime = updatedRecipe.instructions.reduce(
                  (total, instruction) => total + instruction.cooking_time,
                  0
                );

                // Update recipe with new values
                setRecipe({
                  ...updatedRecipe,
                  total_preparing_time: totalPrepTime,
                  total_cooking_time: totalCookTime,
                });
              }}
              formErrors={formErrors}
              mode="instructions"
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <div className=" flex flex-col">
      <PageHeader
        title={id ? "Edit Recipe" : "Create New Recipe"}
        subtitle="Add or update recipe details including ingredients and instructions"
        icon="restaurant_menu"
        onBack={() => navigate("/business/kitchen/recipes")}
        backButton
      />

      <div className="flex-1 flex gap-6 p-6 min-h-0">
        {/* Main Content */}
        <div className="flex-1 flex flex-col bg-white rounded-lg shadow-sm border border-gray-100 min-w-0">
          <Stepper steps={steps} onStepClick={handleStepClick} />

          <div className="flex-1 overflow-auto p-6">{renderStepContent()}</div>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: (theme) => alpha("#0891B2", 0.1),
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  if (currentStep === "01") {
                    navigate("/business/kitchen/recipes");
                  } else {
                    setCurrentStep((prev) => `0${parseInt(prev) - 1}`);
                  }
                }}
                startIcon={<Icon>arrow_back</Icon>}
                sx={{
                  borderColor: "#0891B2",
                  color: "#0891B2",
                  "&:hover": {
                    borderColor: "#0E7490",
                    bgcolor: alpha("#0891B2", 0.04),
                  },
                }}
              >
                {currentStep === "01" ? "Cancel" : "Previous"}
              </Button>
              <LoadingButton
                variant="contained"
                onClick={() => {
                  if (currentStep === "03") {
                    handleSave();
                  } else {
                    setCurrentStep((prev) => `0${parseInt(prev) + 1}`);
                  }
                }}
                loading={saving}
                startIcon={
                  <Icon>{currentStep === "03" ? "save" : "arrow_forward"}</Icon>
                }
                disabled={
                  (currentStep === "01" &&
                    (!recipe.name || !recipe.category)) ||
                  (currentStep === "02" && recipe.ingredients.length === 0)
                }
                sx={{
                  bgcolor: "#0891B2",
                  "&:hover": {
                    bgcolor: "#0E7490",
                  },
                }}
              >
                {currentStep === "03"
                  ? id
                    ? "Update Recipe"
                    : "Create Recipe"
                  : "Next"}
              </LoadingButton>
            </Box>
          </Paper>
        </div>

        {/* Collapsible Time Requirements Sidebar */}
        <div
          className={`${
            isSidebarCollapsed ? "w-12" : "w-80"
          } transition-all duration-300 flex`}
        >
          <div className="flex-1 bg-gray-50 rounded-lg border border-gray-100 flex flex-col overflow-hidden">
            {!isSidebarCollapsed ? (
              <>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2 text-sm text-gray-600 font-medium mb-6">
                    <div className="flex items-center gap-2">
                      <Icon fontSize="small">schedule</Icon>
                      <span>Time Requirements</span>
                    </div>
                    <IconButton
                      onClick={() => setIsSidebarCollapsed(true)}
                      size="small"
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "#0891B2" },
                      }}
                    >
                      <Icon>chevron_right</Icon>
                    </IconButton>
                  </div>

                  <div className="space-y-6">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Preparation</span>
                        <span className="font-medium">
                          {recipe.total_preparing_time} min
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0891B2] rounded-full transition-all duration-300"
                          style={{
                            width: `${Math.min(
                              (recipe.total_preparing_time / 180) * 100,
                              100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Cooking</span>
                        <span className="font-medium">
                          {recipe.total_cooking_time} min
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0891B2] rounded-full transition-all duration-300"
                          style={{
                            width: `${Math.min(
                              (recipe.total_cooking_time / 180) * 100,
                              100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Total Time</span>
                        <span className="font-medium text-[#0891B2]">
                          {recipe.total_preparing_time +
                            recipe.total_cooking_time}{" "}
                          min
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center py-4">
                <IconButton
                  onClick={() => setIsSidebarCollapsed(false)}
                  size="small"
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "#0891B2" },
                  }}
                >
                  <Icon>chevron_left</Icon>
                </IconButton>
                <div className="flex-1 flex items-center mt-4 pt-14">
                  <Typography
                    sx={{
                      transform: "rotate(-90deg)",
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                      fontFamily: "Montserrat",
                      fontSize: "0.75rem",
                    }}
                  >
                    Time Requirements
                  </Typography>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <IngredientDialog
        open={ingredientDialogOpen}
        onClose={handleCloseIngredientDialog}
        onSave={handleSaveIngredient}
        ingredientOptions={ingredientOptions}
        loadingIngredients={loading}
        loadBatches={loadIngredientBatches}
        mode={editingIngredientIndex !== null ? "edit" : "add"}
        currentRecipeIngredients={recipe.ingredients}
        editData={
          editingIngredientIndex !== null
            ? {
                ingredient: ingredientOptions.find(
                  (opt) =>
                    opt.id ===
                    recipe.ingredients[editingIngredientIndex].item_id
                )!,
                quantity: recipe.ingredients[editingIngredientIndex].quantity,
                unit: recipe.ingredients[editingIngredientIndex].unit,
                notes: recipe.ingredients[editingIngredientIndex].notes || "",
                selectedBatch: undefined,
              }
            : undefined
        }
      />
    </div>
  );
};
